import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  Bookmark, 
  Loader2,
  Search,
  Trash2,
  Edit,
  ChevronRight,
  BookOpen
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const volumeLabels = {
  biblia: 'Biblia',
  libro_de_mormon: 'Libro de Mormón',
  doctrina_y_convenios: 'Doctrina y Convenios',
  perla_de_gran_precio: 'Perla de Gran Precio'
};

const colorOptions = [
  { value: 'gold', label: 'Dorado', class: 'bg-yellow-500' },
  { value: 'blue', label: 'Azul', class: 'bg-blue-500' },
  { value: 'green', label: 'Verde', class: 'bg-green-500' },
  { value: 'purple', label: 'Púrpura', class: 'bg-purple-500' },
  { value: 'red', label: 'Rojo', class: 'bg-red-500' },
];

const BookmarksPage = () => {
  const { token } = useAuth();

  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVolume, setSelectedVolume] = useState('all');
  
  const [editingBookmark, setEditingBookmark] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editNote, setEditNote] = useState('');
  const [editColor, setEditColor] = useState('gold');
  const [saving, setSaving] = useState(false);

  const fetchBookmarks = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/bookmarks`;
      if (selectedVolume && selectedVolume !== 'all') {
        url += `?volume=${selectedVolume}`;
      }
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBookmarks(data);
      } else {
        toast.error('Error al cargar los marcadores');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  }, [token, selectedVolume]);

  useEffect(() => {
    fetchBookmarks();
  }, [fetchBookmarks]);

  const handleEdit = (bookmark) => {
    setEditingBookmark(bookmark);
    setEditNote(bookmark.note || '');
    setEditColor(bookmark.color || 'gold');
    setEditDialogOpen(true);
  };

  const saveEdit = async () => {
    if (!editingBookmark) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/bookmarks/${editingBookmark.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          note: editNote,
          color: editColor
        })
      });

      if (response.ok) {
        toast.success('Marcador actualizado');
        fetchBookmarks();
        setEditDialogOpen(false);
      } else {
        toast.error('Error al actualizar');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setSaving(false);
    }
  };

  const deleteBookmark = async (bookmarkId) => {
    try {
      const response = await fetch(`${API_URL}/api/bookmarks/${bookmarkId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Marcador eliminado');
        fetchBookmarks();
      } else {
        toast.error('Error al eliminar');
      }
    } catch (error) {
      toast.error('Error de conexión');
    }
  };

  const filteredBookmarks = bookmarks.filter(bookmark => {
    const matchesSearch = 
      bookmark.verse_text.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bookmark.book_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (bookmark.note && bookmark.note.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesSearch;
  });

  const getColorClass = (color) => {
    const option = colorOptions.find(c => c.value === color);
    return option ? option.class : 'bg-yellow-500';
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="bookmarks-page">
        {/* Header */}
        <div className="flex flex-col gap-2">
          <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
            <Bookmark className="h-8 w-8 text-primary" />
            Mis Marcadores
          </h1>
          <p className="text-muted-foreground">
            {bookmarks.length} versículos marcados
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar en marcadores..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              data-testid="search-bookmarks"
            />
          </div>
          <Select value={selectedVolume} onValueChange={setSelectedVolume}>
            <SelectTrigger className="w-full sm:w-[200px]" data-testid="volume-filter">
              <SelectValue placeholder="Filtrar por volumen" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los volúmenes</SelectItem>
              <SelectItem value="biblia">Biblia</SelectItem>
              <SelectItem value="libro_de_mormon">Libro de Mormón</SelectItem>
              <SelectItem value="doctrina_y_convenios">Doctrina y Convenios</SelectItem>
              <SelectItem value="perla_de_gran_precio">Perla de Gran Precio</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Bookmarks List */}
        {filteredBookmarks.length > 0 ? (
          <div className="grid gap-4">
            {filteredBookmarks.map((bookmark) => (
              <Card key={bookmark.id} className="overflow-hidden">
                <div className={`h-1 ${getColorClass(bookmark.color)}`} />
                <CardContent className="pt-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <Link to={`/study/${bookmark.volume}/${bookmark.book}/${bookmark.chapter}`}>
                          <Badge 
                            variant="secondary" 
                            className="cursor-pointer hover:bg-primary/20 transition-colors"
                          >
                            {bookmark.book_name} {bookmark.chapter}:{bookmark.verse}
                          </Badge>
                        </Link>
                        <Badge variant="outline" className="text-xs">
                          {volumeLabels[bookmark.volume]}
                        </Badge>
                      </div>
                      <p className="scripture-text text-sm text-foreground line-clamp-2 mb-2">
                        {bookmark.verse_text}
                      </p>
                      {bookmark.note && (
                        <p className="text-xs text-muted-foreground italic">
                          Nota: {bookmark.note}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Link to={`/study/${bookmark.volume}/${bookmark.book}/${bookmark.chapter}`}>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </Link>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleEdit(bookmark)}
                        data-testid={`edit-bookmark-${bookmark.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive"
                            data-testid={`delete-bookmark-${bookmark.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>¿Eliminar marcador?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Se eliminará el marcador de {bookmark.book_name} {bookmark.chapter}:{bookmark.verse}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteBookmark(bookmark.id)}>
                              Eliminar
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <Bookmark className="h-16 w-16 mb-4 opacity-20" />
              <p className="text-lg font-medium">No hay marcadores</p>
              <p className="text-sm text-center max-w-md">
                {searchTerm || selectedVolume !== 'all'
                  ? 'No se encontraron marcadores con los filtros aplicados'
                  : 'Marca versículos mientras estudias para guardarlos aquí'}
              </p>
              <Link to="/scriptures" className="mt-4">
                <Button variant="outline" className="gap-2">
                  <BookOpen className="h-4 w-4" />
                  Ir a Escrituras
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Editar Marcador</DialogTitle>
              <DialogDescription>
                {editingBookmark && `${editingBookmark.book_name} ${editingBookmark.chapter}:${editingBookmark.verse}`}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Nota personal</Label>
                <Textarea
                  value={editNote}
                  onChange={(e) => setEditNote(e.target.value)}
                  placeholder="Añade una nota sobre este versículo..."
                  className="min-h-[100px]"
                  data-testid="edit-note-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Color del marcador</Label>
                <div className="flex gap-2">
                  {colorOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`w-8 h-8 rounded-full ${option.class} transition-transform ${
                        editColor === option.value ? 'ring-2 ring-offset-2 ring-primary scale-110' : ''
                      }`}
                      onClick={() => setEditColor(option.value)}
                      title={option.label}
                    />
                  ))}
                </div>
              </div>
              <Button 
                onClick={saveEdit} 
                disabled={saving} 
                className="w-full"
                data-testid="save-bookmark-edit"
              >
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Guardar Cambios
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};

export default BookmarksPage;
